import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  Plus, 
  Trash2, 
  Users, 
  User, 
  Calendar,
  Edit2,
  UserPlus 
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  collection, 
  getDocs, 
  doc, 
  deleteDoc, 
  addDoc, 
  updateDoc,
  query, 
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  createdAt: Date;
  createdBy: string;
  createdByName?: string;
  isActive: boolean;
  members?: string[];
}

interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  firstNameEn?: string;
  lastNameEn?: string;
  email: string;
}

export function AdminGroupManagement() {
  const { t, dir } = useLanguage();
  const [groups, setGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    isActive: true
  });

  const fetchGroups = async () => {
    try {
      setLoading(true);
      
      // Fetch groups
      const groupsRef = collection(db, 'groups');
      const groupsQuery = query(groupsRef, orderBy('createdAt', 'desc'));
      const groupsSnapshot = await getDocs(groupsQuery);
      
      // Fetch users to get creator names
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersMap = new Map();
      const allUsers: User[] = [];
      
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        const user = {
          id: doc.id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          firstNameEn: userData.firstNameEn,
          lastNameEn: userData.lastNameEn,
          email: userData.email
        };
        allUsers.push(user);
        
        const name = `${userData.firstNameEn || userData.firstName || ''} ${userData.lastNameEn || userData.lastName || ''}`.trim();
        usersMap.set(doc.id, name || userData.email || 'Unknown User');
      });

      setUsers(allUsers);

      const groupsData = groupsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          createdByName: usersMap.get(data.createdBy) || 'Unknown User',
          memberCount: data.members?.length || 0
        } as Group;
      });

      setGroups(groupsData);
      setFilteredGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error(t('dashboard.admin.groups.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter(group =>
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.createdByName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredGroups(filtered);
    }
  }, [searchQuery, groups]);

  const handleDeleteClick = (group: Group) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'groups', groupToDelete.id));
      
      setGroups(prev => prev.filter(g => g.id !== groupToDelete.id));
      toast.success(t('dashboard.admin.groups.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(t('dashboard.admin.groups.messages.deleteError'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      setCreating(true);
      const groupData = {
        ...newGroup,
        createdAt: new Date(),
        createdBy: 'admin', // You might want to use actual admin user ID
        members: [],
        memberCount: 0
      };

      const docRef = await addDoc(collection(db, 'groups'), groupData);
      
      const newGroupWithId = {
        id: docRef.id,
        ...groupData,
        createdByName: 'Admin'
      };

      setGroups(prev => [newGroupWithId, ...prev]);
      setNewGroup({ name: '', description: '', isActive: true });
      setCreateDialogOpen(false);
      toast.success(t('dashboard.admin.groups.messages.createSuccess'));
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error(t('dashboard.admin.groups.messages.createError'));
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (group: Group) => {
    setEditingGroup(group);
    setNewGroup({
      name: group.name,
      description: group.description,
      isActive: group.isActive
    });
    setEditDialogOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !newGroup.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      setUpdating(true);
      await updateDoc(doc(db, 'groups', editingGroup.id), {
        name: newGroup.name,
        description: newGroup.description,
        isActive: newGroup.isActive
      });

      setGroups(prev => prev.map(g => 
        g.id === editingGroup.id 
          ? { ...g, name: newGroup.name, description: newGroup.description, isActive: newGroup.isActive }
          : g
      ));

      setEditDialogOpen(false);
      setEditingGroup(null);
      setNewGroup({ name: '', description: '', isActive: true });
      toast.success(t('dashboard.admin.groups.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error(t('dashboard.admin.groups.messages.updateError'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('dashboard.admin.groups.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.groups.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.admin.groups.pageDescription')}
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4`} />
              {t('dashboard.admin.groups.createGroup')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('dashboard.admin.groups.createGroup')}</DialogTitle>
              <DialogDescription>
                {t('dashboard.admin.groups.createGroupDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">{t('dashboard.admin.groups.groupName')}</Label>
                <Input
                  id="name"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('dashboard.admin.groups.groupNamePlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="description">{t('dashboard.admin.groups.description')}</Label>
                <Textarea
                  id="description"
                  value={newGroup.description}
                  onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('dashboard.admin.groups.descriptionPlaceholder')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                {t('dashboard.admin.groups.cancel')}
              </Button>
              <Button onClick={handleCreateGroup} disabled={creating}>
                {creating ? t('dashboard.admin.groups.creating') : t('dashboard.admin.groups.create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.admin.groups.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredGroups.length} {t('dashboard.admin.groups.of')} {groups.length} {t('dashboard.admin.groups.groupsCount')}
        </div>
      </div>

      {/* Groups Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('dashboard.admin.groups.allGroups')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredGroups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? t('dashboard.admin.groups.noGroupsFound') : t('dashboard.admin.groups.noGroups')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.admin.groups.tableHeaders.group')}</TableHead>
                    <TableHead>{t('dashboard.admin.groups.tableHeaders.members')}</TableHead>
                    <TableHead>{t('dashboard.admin.groups.tableHeaders.status')}</TableHead>
                    <TableHead>{t('dashboard.admin.groups.tableHeaders.createdBy')}</TableHead>
                    <TableHead>{t('dashboard.admin.groups.tableHeaders.created')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.admin.groups.tableHeaders.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{group.name}</div>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {group.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {group.memberCount}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={group.isActive ? "default" : "secondary"}>
                          {group.isActive ? t('dashboard.admin.groups.status.active') : t('dashboard.admin.groups.status.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {group.createdByName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {group.createdAt.toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(group)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(group)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Group Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.groups.editGroup')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.groups.editGroupDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">{t('dashboard.admin.groups.groupName')}</Label>
              <Input
                id="edit-name"
                value={newGroup.name}
                onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('dashboard.admin.groups.groupNamePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">{t('dashboard.admin.groups.description')}</Label>
              <Textarea
                id="edit-description"
                value={newGroup.description}
                onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('dashboard.admin.groups.descriptionPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('dashboard.admin.groups.cancel')}
            </Button>
            <Button onClick={handleUpdateGroup} disabled={updating}>
              {updating ? t('dashboard.admin.groups.updating') : t('dashboard.admin.groups.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboard.admin.groups.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard.admin.groups.deleteDialog.description', { name: groupToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboard.admin.groups.deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? t('dashboard.admin.groups.deleteDialog.deleting') : t('dashboard.admin.groups.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
